import { useInfiniteQuery } from "@tanstack/react-query";

const BASE = "/api";

export interface InboxConversationSummary {
  id: string;
  brandId: string;
  socialAccountId: string;
  platform: string;
  zernioConvId: string;
  participantId: string;
  participantName: string | null;
  participantAvatar: string | null;
  status: "OPEN" | "SNOOZED" | "DONE" | "ARCHIVED";
  assignedUserId: string | null;
  assignedUser: { id: string; name: string | null; email: string } | null;
  lastMessageAt: string;
  lastMessageText: string | null;
  snoozedUntil: string | null;
  unreadCount: number;
  socialAccount: { id: string; platform: string; platformUsername: string | null } | null;
  createdAt: string;
}

interface Page {
  conversations: InboxConversationSummary[];
  nextCursor: string | null;
}

interface UseInboxConversationsParams {
  brandId: string;
  status?: "OPEN" | "SNOOZED" | "DONE" | "ARCHIVED";
  assigned?: string; // "me" | "unassigned" | userId
  q?: string;
  platform?: string; // channel filter; undefined = all platforms
}

async function fetchPage(params: UseInboxConversationsParams, cursor?: string): Promise<Page> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.assigned) search.set("assigned", params.assigned);
  if (params.q) search.set("q", params.q);
  if (params.platform) search.set("platform", params.platform);
  if (cursor) search.set("cursor", cursor);
  const url = `${BASE}/brands/${params.brandId}/inbox/conversations?${search.toString()}`;
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error("Failed to load conversations");
  const json = (await r.json()) as { data: Page };
  return json.data;
}

export function useInboxConversations(params: UseInboxConversationsParams) {
  return useInfiniteQuery({
    queryKey: ["inbox-conversations", params.brandId, params.status, params.assigned, params.q, params.platform],
    queryFn: ({ pageParam }) => fetchPage(params, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!params.brandId,
    staleTime: 15_000,
  });
}
